// @givmo/board — a seat's history, as the roster recorded it (CBG-SEC2 SEC2-BE1b; contract §4.1).
//
// Every change to a seat is an append-only event: added, its details or address changed, an invitation
// sent, joined with an account, disconnected, recorded as former. It closes the one quiet path an Owner
// had (re-address a seat to an inbox they control, invite, join): each step now leaves a line here
// and the old address gets a notice. Owners read it on the member page; no director surface does.
//
// The rows are the generated `BoardRosterEventOut` (SEC2-BE1b's route, generated since the one
// regeneration), read tolerantly: a malformed row is dropped, and an event type this build doesn't
// know still reads as a line.
//
// THE WORDS. Approved as copy round 2's page showed them (D-CBG-18): "Added with <address> by
// <name>", "Name and seat changed by <name>", "Email changed from <address> to <address> by
// <name>", "Invited by <name> (emailed)" and "(no email sent)", "Joined with <account> (code sent
// to the seat’s address)" and "(address matched)", "Disconnected by <name>: <reason>", "Recorded
// as former by <name>: <reason>", and "someone in this organization" for a name the directory
// can't resolve. Built from those words and adding none: one field alone ("Seat changed by
// <name>"), and "a Givmo account" for an account the directory can't name (round 2's own fallback
// in the security emails). The field word "term" is approved too: "Term changed by <name>" (copy
// round 3, CBG-SEC2-COPY3, D-CBG-18). The fallbacks below for a row missing its address, flag,
// method or fields never show: the roster's CHECKs (`ck_board_roster_event_*`) forbid such a row.
//
// OFFICER ROLES (CBG-YR3-ROLES layer 1) add three lines, from `officer-roles-words.ts`: an update
// that named director status says which way it went ("Recorded as not a director, and titles
// changed, by <name>", from the event's `is_director_after`), "titles" joins the field words, and a
// title the migration carried over from the old chair or secretary seat is a line read off the
// seat's own titles, merged in by its date (`carriedOverEntries`, `mergeHistory`).
//
// AN INVITATION WHOSE EMAIL DIDN'T GO OUT (CBG-FIX75; backend `board-committee-confirmation.md`
// §11): the invitation's own entry says whether the module meant to mail it, decided before the send,
// and the backend folds a later entry into it when the mail didn't go out (refused, failed, or never
// reached by the send budget): "Invited by <name> (the email didn't go out)", approved with cut
// confirm's words (CBG-YR6-COPY1's next part, 2026-10-16 19:35 PDT); every line above is approved.
//
// A DIRECTOR'S MOVE (CBG-SEC2-L4-MOVE; D-CBG-23 flow (b), Jonathan's answer 3: no email to the
// board's Owners, "the History line instead"): "Moved to another Givmo account by <name> (code sent
// to the seat’s address)" or "(address matched)", in the shape of round 2's "Joined with …" (frame
// b8, approved in copy round CBG-SEC2-L4-COPY1). It names neither account, nor why: the
// organization behind a lock is the director's own business. `movedHere` tells the "Joining the
// board" card the seat's account came by a move.
//
// A SEAT ADDED FROM A PROPOSAL (CBG-AI5-PROPOSE; backend `board-ai-proposals.md` §7): its "Added …"
// line goes on to name the proposal (copy 7.3, approved in CBG-AI5-PROPOSE-COPY1), from the entry's
// own lineage fields: "Added with <address> by <name> · proposed through <client> for <name> on
// <date>".
//
// A SEAT RELEASED BECAUSE ITS ORGANIZATION CLOSED (CBG-FIX110): the organization was deactivated, or
// its removal from Givmo was processed, and Board disconnected the account. Each line names no one,
// like the escape hatch's: the closing act's actor (a Global Admin, or a Givmo super-admin for a
// removal) is never one of the board's officers, and the backend serves it on no read. Approved in
// the wayfinding copy round (§9.1, 2026-10-24), and reworded in CBG-UX-COPY2 (D-CBG-42, A2) to say
// whose organization, and that a removal was its own request: "Disconnected when this organization
// was deactivated on Givmo" and "Disconnected when this organization was removed from Givmo at its
// request", word for word the export's (backend `board_export_service.py`).

import type { BoardRosterEventOut } from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import { displayInstant, displayLongDate, humanize } from '../../lib/display';
import { proposalWords as pw } from '../proposals/proposal-words';
import { carriedOverSeatWord, seatTitles, type SeatFacts } from './officer-roles';
import { officerRolesWords as w } from './officer-roles-words';
import { boardJoiningWords as bj } from './board-joining-words';

export interface RosterHistoryEntry {
  id: string;
  eventType: string;
  occurredAt: string | null;
  actorName: string | null;
  accountName: string | null;
  claimMethod: string | null;
  emailBefore: string | null;
  emailAfter: string | null;
  invitationEmailed: boolean | null;
  /** Whether a later entry says the invitation's email didn't go out (CBG-FIX75); null when the
   *  module never meant to mail it, or on an older backend. */
  invitationEmailNotSent: boolean | null;
  changedFields: string[];
  reason: string | null;
  /** Director status after the change, on an update that named it (CBG-YR3-ROLES); else null. */
  isDirectorAfter: boolean | null;
  /** A title carried over from the old chair or secretary seat, and the seat word it came from. */
  carriedOver: { label: string; seat: string } | null;
  /** The board buddy a `buddy_chosen` entry names, as the roster names them (CBG-YR4-START). */
  buddyName: string | null;
  /**
   * CBG-AI5-PROPOSE: on a `seat_added` entry of a seat added from a connected AI's proposal, the
   * client it came through, whose connection it was (null when the directory can't name them) and
   * when it was proposed; else null.
   */
  proposal: { proposerName: string | null; proposedAt: string | null } | null;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

/** The history read's answer, newest first as the backend orders it; a malformed row is dropped. */
export function extractRosterHistory(body: unknown): RosterHistoryEntry[] {
  const rows = extractList<BoardRosterEventOut>(body, 'data');
  const entries: RosterHistoryEntry[] = [];
  for (const row of rows) {
    if (typeof row !== 'object' || row === null) continue;
    const id = text(row.id);
    const eventType = text(row.event_type);
    if (id === null || eventType === null) continue;
    entries.push({
      id,
      eventType,
      occurredAt: text(row.occurred_at),
      actorName: text(row.actor_display_name),
      accountName: text(row.account_display_name),
      claimMethod: text(row.claim_method),
      emailBefore: text(row.email_of_record_before),
      emailAfter: text(row.email_of_record_after),
      invitationEmailed:
        typeof row.invitation_emailed === 'boolean' ? row.invitation_emailed : null,
      invitationEmailNotSent:
        typeof row.invitation_email_not_sent === 'boolean' ? row.invitation_email_not_sent : null,
      changedFields: Array.isArray(row.changed_fields)
        ? row.changed_fields.filter((field): field is string => typeof field === 'string')
        : [],
      reason: text(row.reason),
      isDirectorAfter: typeof row.is_director_after === 'boolean' ? row.is_director_after : null,
      carriedOver: null,
      buddyName: text(row.buddy_display_name),
      proposal:
        text(row.proposal_id) === null
          ? null
          : {
              proposerName: text(row.proposed_by_display_name),
              proposedAt: text(row.proposed_at),
            },
    });
  }
  return entries;
}

/**
 * The seat's titles carried over from its old chair or secretary seat, as history lines.
 *
 * Officer roles layer 1's migration gave a seat recorded as chair or secretary that title, with no
 * history entry of its own: the roster history needs an actor, and the carry-over had none. The
 * assignment says so instead (`provenance: carried_over`, dated the migration's instant), so the line
 * is read off the seat's own `titles[]`. It claims no officer term: it says where the title came from.
 */
export function carriedOverEntries(seat: SeatFacts & { id: string }): RosterHistoryEntry[] {
  const entries: RosterHistoryEntry[] = [];
  for (const title of seatTitles(seat)) {
    const word = carriedOverSeatWord(title);
    if (word === null) continue;
    entries.push({
      id: `carried-over-${title.id}`,
      eventType: 'title_carried_over',
      occurredAt: title.assigned_at,
      actorName: null,
      accountName: null,
      claimMethod: null,
      emailBefore: null,
      emailAfter: null,
      invitationEmailed: null,
      invitationEmailNotSent: null,
      changedFields: [],
      reason: null,
      isDirectorAfter: null,
      carriedOver: { label: title.label, seat: word },
      buddyName: null,
      proposal: null,
    });
  }
  return entries;
}

/** Two newest-first lists merged newest first; an entry with no instant goes last. */
export function mergeHistory(
  recorded: readonly RosterHistoryEntry[],
  derived: readonly RosterHistoryEntry[],
): RosterHistoryEntry[] {
  const at = (entry: RosterHistoryEntry) => {
    const value = entry.occurredAt === null ? Number.NaN : Date.parse(entry.occurredAt);
    return Number.isNaN(value) ? Number.NEGATIVE_INFINITY : value;
  };
  return [...recorded, ...derived]
    .map((entry, index) => ({ entry, index }))
    .sort((left, right) => at(right.entry) - at(left.entry) || left.index - right.index)
    .map(({ entry }) => entry);
}

const FIELD_WORDS: Record<string, string> = {
  display_name: 'name',
  board_role: 'seat',
  // "Term changed by …", which an officer's change to a seat's term writes: approved as written
  // (copy round 3, CBG-SEC2-COPY3, D-CBG-18).
  term_start_on: 'term',
  term_end_on: 'term',
  // CBG-YR3-TERMS: every change to a seat's terms names `terms` (backend `board-terms.md` §5.4) — the
  // same approved word, so "Term changed by …" reads as it always did.
  terms: 'term',
  // CBG-YR3-ROLES layer 1: the two facts an update can name since then (it never names `board_role`).
  is_director: w.fieldDirector,
  titles: w.fieldTitles,
};

/** "Name and seat", from the fields an update named (never their values). */
function changedWords(fields: readonly string[]): string {
  const words = [
    ...new Set(fields.map((field) => FIELD_WORDS[field] ?? humanize(field).toLowerCase())),
  ];
  if (words.length === 0) return 'Details';
  const phrase =
    words.length === 1
      ? words[0]!
      : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
  return phrase.charAt(0).toUpperCase() + phrase.slice(1);
}

/** How the seat's address was proved, as a joining or a move says it (copy round 2's words). */
function claimMethodWords(method: string | null): string {
  return method === 'email_match'
    ? ' (address matched)'
    : method === 'email_code'
      ? ' (code sent to the seat’s address)'
      : '';
}

/** The events that say how the seat's current account came to it, or that it lost one. */
const CUSTODY_EVENTS = new Set([
  'seat_claimed',
  'account_moved',
  'account_released',
  'account_released_on_escape',
  'account_released_on_deactivation',
  'account_released_on_removal',
]);

/**
 * Whether the seat's account came to it by its director's move (CBG-SEC2-L4-MOVE): the newest
 * custody event in a newest-first history is the move. "Joining the board" then says "Moved to …"
 * (frame b8), dated by the seat's link, which a move restamps.
 */
export function movedHere(entries: readonly RosterHistoryEntry[]): boolean {
  return (
    entries.find((entry) => CUSTODY_EVENTS.has(entry.eventType))?.eventType === 'account_moved'
  );
}

/**
 * One entry as one line, in the words the header names (copy round 2's, and round 3's "term").
 * The actor is named when the directory could resolve them.
 */
export function rosterHistoryLine(entry: RosterHistoryEntry): string {
  const by = ` by ${entry.actorName ?? 'someone in this organization'}`;
  switch (entry.eventType) {
    case 'seat_added': {
      const added =
        entry.emailAfter === null ? `Added${by}` : `Added with ${entry.emailAfter}${by}`;
      if (entry.proposal === null) return added;
      return `${added}${pw.seatLineage(
        entry.proposal.proposerName ?? 'someone in this organization',
        displayLongDate(entry.proposal.proposedAt) ?? '—',
      )}`;
    }
    case 'seat_updated': {
      // CBG-YR6-OUTSIDE-WIDEN (frame w4): the act that adds someone who isn't on the board to it
      // writes one entry naming `scope`, alone (the backend's `ck_board_roster_event_scope_alone`).
      if (entry.changedFields.includes('scope')) return bj.historyLine(by);
      if (entry.changedFields.includes('is_director') && entry.isDirectorAfter !== null) {
        // Director status says which way it went; any other field it changed beside it follows. A
        // row that names the field without saying where it went claims no direction: "Director
        // status changed by …", the field words' own pattern.
        const lead = entry.isDirectorAfter === false ? w.historyNotDirector : w.historyDirector;
        const rest = entry.changedFields.filter((field) => field !== 'is_director');
        if (rest.length === 0) return `${lead}${by}`;
        const others =
          rest.length === 1 && rest[0] === 'titles'
            ? w.historyTitlesChanged
            : `${changedWords(rest).toLowerCase()} changed`;
        return `${lead}, and ${others},${by}`;
      }
      return `${changedWords(entry.changedFields)} changed${by}`;
    }
    case 'title_carried_over':
      return w.carriedOver(entry.carriedOver?.label ?? '', entry.carriedOver?.seat ?? '');
    case 'email_of_record_changed':
      return `Email changed from ${entry.emailBefore ?? '—'} to ${entry.emailAfter ?? '—'}${by}`;
    case 'invitation_sent':
      return `Invited${by}${
        entry.invitationEmailed === true && entry.invitationEmailNotSent === true
          ? // CBG-FIX75: meant to be mailed, and the email didn't go out.
            ' (the email didn’t go out)'
          : entry.invitationEmailed === true
            ? ' (emailed)'
            : entry.invitationEmailed === false
              ? ' (no email sent)'
              : ''
      }`;
    case 'seat_claimed':
      return `Joined with ${entry.accountName ?? 'a Givmo account'}${claimMethodWords(entry.claimMethod)}`;
    case 'account_moved':
      // Frame b8.
      return `Moved to another Givmo account${by}${claimMethodWords(entry.claimMethod)}`;
    case 'account_released':
      return `Disconnected${by}${entry.reason === null ? '' : `: ${entry.reason}`}`;
    case 'seat_removed':
      return `Recorded as former${by}${entry.reason === null ? '' : `: ${entry.reason}`}`;
    case 'buddy_chosen':
      // CBG-YR4-START (approved, CBG-YR4-COPY1's first part): the buddy an officer chose, named
      // when the roster can.
      return `Board buddy chosen${by}${entry.buddyName === null ? '' : `: ${entry.buddyName}`}`;
    case 'account_released_on_deactivation':
      // CBG-FIX110: no "by …", whatever the row carries — the closing act's actor is served on no read.
      return 'Disconnected when this organization was deactivated on Givmo';
    case 'account_released_on_removal':
      return 'Disconnected when this organization was removed from Givmo at its request';
    default:
      return `${humanize(entry.eventType)}${by}`;
  }
}

/** When it happened, at the grain an entry is recorded at. */
export function rosterHistoryWhen(entry: RosterHistoryEntry): string {
  return displayInstant(entry.occurredAt);
}
